import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { NotificationBell } from "./NotificationBell";
import { UserMenu } from "./UserMenu";

const SEGMENT_LABEL: Record<string, string> = {
  dashboard: "Dashboard",
  companies: "Companies",
  contacts: "Contacts",
  engine: "Engine Control",
  runs: "Runs",
  settings: "Settings",
  account: "Account settings",
  tasks: "Tasks",
  notifications: "Notifications",
};

function labelFor(segment: string, previous?: string): string {
  if (SEGMENT_LABEL[segment]) return SEGMENT_LABEL[segment];
  // Numeric ids get "Detail" label — an Email campaign's id, "Campaign".
  if (/^\d+$/.test(segment)) return previous === "campaigns" ? "Campaign" : "Detail";
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

// The global search moved to the top of the sidebar (Prism task #268).
export function Header() {
  const location = useLocation();
  const { project } = useCurrentProject();

  const segments = location.pathname.split("/").filter(Boolean);
  // Drop "/projects/:slug" (first 2 segments) when building breadcrumbs.
  const projectIdx = segments.indexOf("projects");
  const trail =
    projectIdx >= 0 ? segments.slice(projectIdx + 2) : segments.slice();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200/70 bg-slate-50/90 px-8 backdrop-blur-md">
      <div className="flex min-w-0 items-center gap-2 overflow-hidden text-sm">
        <Link
          to={project ? `/projects/${project.slug}/dashboard` : "/projects"}
          className="max-w-[14rem] truncate whitespace-nowrap font-medium text-slate-500 transition-colors hover:text-slate-900"
        >
          {project?.name ?? "Prism"}
        </Link>
        {trail.map((seg, i) => (
          <span key={`${seg}-${i}`} className="flex shrink-0 items-center gap-2 whitespace-nowrap">
            <ChevronRight className="h-4 w-4 text-slate-300" />
            <span
              className={
                i === trail.length - 1
                  ? "font-semibold text-slate-900"
                  : "text-slate-500"
              }
            >
              {labelFor(seg, trail[i - 1])}
            </span>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-3">
        {/* The bell sits 2px lower so its visual center lines up
            with the avatar circle in the UserMenu trigger to the
            right — the bell's box (p-2 + h-5 icon = 36px) is taller
            than the avatar (32px), so a pure flex items-center
            leaves it visually high. */}
        <div className="translate-y-0.5">
          <NotificationBell />
        </div>
        <UserMenu />
      </div>
    </header>
  );
}
